import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, DatePicker, Checkbox, Row, Col, Typography, message, Table, Select, Space } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { useLaboratoryId } from '../hooks/useLaboratoryId';
import { createReagent } from '../api/chemicalRegistry';
import type { ColumnsType } from 'antd/es/table';
import { getCustomColumns, CustomColumn } from "../api/customColumns";
import { getChemicalNames } from "../api/chemicals";

const { Text } = Typography;
const { Option } = Select;

const StyledButton = styled(Button)`
  height: 40px;
  width: 150px;
  border-radius: 4px;
`;

const ButtonsRow = styled(Row)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

interface DataType {
  key: React.Key;
  number: number;
  name: string;
  batch: string;
  manufacturer: string;
  supplier: string;
  receipt_date: dayjs.Dayjs;
  expiration_date: dayjs.Dayjs;
  storage_location: string;
  storage_conditions: string;
  container_size: string;
  container_unit: string;
  quantity: string;
  unit: string;
  opened_shelf_life: string;
  frequency_class: string;
  has_documents: boolean;
  has_admission: boolean;
  is_precursor: boolean;
  used: string;
  remaining: string;
  notes: string;
  minimum_remaining: string;
  custom_fields?: Record<string, any>;
}

// Стилизованная таблица
const StyledTable = styled(Table)`
  .ant-table-tbody > tr > td {
    height: 55px;
    padding: 8px;
  }
  
  .ant-table-body {
    height: 260px !important;
    overflow-y: auto !important;
  }
`;

interface MultipleReagentsAddProps {
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
}

// Компоненты для ячеек таблицы
const CellInput = React.memo(({ value, onChange }: { value: string, onChange: (value: string) => void }) => (
  <Input value={value} onChange={(e) => onChange(e.target.value)} style={{ width: '100%' }} />
));

const CellNumberInput = React.memo(({ value, onChange }: { value: string, onChange: (value: string) => void }) => (
  <Input
    type="number"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    style={{ width: '100%' }}
  />
));

const CellSelect = React.memo(({ value, onChange, options }: { value: string, onChange: (value: string) => void, options: { label: string, value: string }[] }) => (
  <Select
    value={value}
    onChange={onChange}
    options={options}
    style={{ width: '100%' }}
    showSearch
    allowClear
  >
    {options.map((option) => (
      <Option key={option.value} value={option.value}>
        {option.label}
      </Option>
    ))}
  </Select>
));

const CellDatePicker = React.memo(({ value, onChange }: { value: dayjs.Dayjs | null, onChange: (value: dayjs.Dayjs | null) => void }) => (
  <DatePicker
    value={value}
    onChange={onChange}
    format="DD.MM.YYYY"
    style={{ width: '100%' }}
  />
));

const CellCheckbox = React.memo(({ checked, onChange }: { checked: boolean, onChange: (checked: boolean) => void }) => (
  <Checkbox checked={checked} onChange={(e) => onChange(e.target.checked)} />
));

// Опции для селектов
const frequencyClassOptions = [
  'Тех',
  'Ч',
  'ЧДА',
  'ХЧ',
  'СЧ',
  'ОСЧ'
].map(item => ({ label: item, value: item }));

const containerUnitOptions = [
  'г',
  'кг',
  'мл',
  'л',
  'шт'
].map(item => ({ label: item, value: item }));

const locationOptions = [
  'Склад A',
  'Склад B',
  'Склад C',
  'Другое'
].map(item => ({ label: item, value: item }));

const manufacturerOptions = [
  'Поизводитель 1',
  'Производитель 2',
  'Производитель 3',
].map(item => ({ label: item, value: item }));

const supplierOptions = [
  'Поставщик 1',
  'Поставщик 2',
  'Поставщик 3',
].map(item => ({ label: item, value: item }));

const MultipleReagentsAdd: React.FC<MultipleReagentsAddProps> = ({
  open,
  onCancel,
  onSuccess
}) => {
  const [dataSource, setDataSource] = useState<DataType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const labId = useLaboratoryId();
  const [chemicalNames, setChemicalNames] = useState<string[]>([]);
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const names = await getChemicalNames();
        setChemicalNames(names);
      } catch (error) {
        console.error("Error fetching chemical names:", error);
        message.error("Не удалось загрузить список химических веществ");
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchCustomColumns = async () => {
      try {
        const columns = await getCustomColumns('reagent', Number(labId));
        setCustomColumns(columns);
      } catch (error) {
        console.error("Error fetching custom columns:", error);
        message.error("Не удалось загрузить пользовательские колонки");
      }
    };

    if (labId) {
      fetchCustomColumns();
    }
  }, [labId]);

  const handleInputChange = (key: React.Key, field: string, value: any) => {
    setDataSource(prev => prev.map(item => {
      if (item.key === key) {
        return {
          ...item,
          [field]: value
        };
      }
      return item;
    }));
  };

  const handleAddRow = () => {
    const newKey = Date.now().toString();
    const newRow: DataType = {
      key: newKey,
      number: dataSource.length + 1,
      name: '',
      batch: '',
      manufacturer: '',
      supplier: '',
      receipt_date: dayjs(),
      expiration_date: dayjs(),
      storage_location: '',
      storage_conditions: '',
      container_size: '',
      container_unit: '',
      quantity: '',
      unit: '',
      opened_shelf_life: '',
      frequency_class: '',
      has_documents: false,
      has_admission: false,
      is_precursor: false,
      used: '0',
      remaining: '0',
      notes: '',
      minimum_remaining: '10',
      custom_fields: {}
    };
    
    setDataSource([...dataSource, newRow]);
  };

  const handleDelete = (key: React.Key) => {
    setDataSource(prev => prev.filter(item => item.key !== key));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Проверяем обязательные поля
      const requiredFields = [
        'name',
        'batch',
        'manufacturer',
        'supplier',
        'receipt_date',
        'expiration_date',
        'storage_location',
        'quantity',
        'container_size',
        'container_unit'
      ];

      // Находим первую запись с пустыми обязательными полями
      const recordWithMissingFields = dataSource.find(item => 
        requiredFields.some(field => {
          const value = item[field as keyof DataType];
          return value === undefined || value === null || value === '';
        })
      );

      if (recordWithMissingFields) {
        // Находим какие именно поля пустые
        const missingFieldNames = requiredFields.filter(field => {
          const value = recordWithMissingFields[field as keyof DataType];
          return value === undefined || value === null || value === '';
        });

        message.error(`Пожалуйста, заполните следующие обязательные поля: ${missingFieldNames.join(', ')}`);
        return;
      }

      // Преобразуем данные для отправки
      const reagentsToCreate = dataSource.map(item => ({
        name: item.name,
        batch: item.batch,
        manufacturer: item.manufacturer,
        supplier: item.supplier,
        receipt_date: item.receipt_date.format('YYYY-MM-DD'),
        expiration_date: item.expiration_date.format('YYYY-MM-DD'),
        storage_location: item.storage_location,
        storage_conditions: item.storage_conditions,
        container_size: parseFloat(item.container_size),
        container_unit: item.container_unit,
        quantity: parseFloat(item.quantity),
        unit: item.unit,
        opened_shelf_life: parseInt(item.opened_shelf_life) || null,
        frequency_class: item.frequency_class,
        has_documents: item.has_documents,
        has_admission: item.has_admission,
        is_precursor: item.is_precursor,
        used: parseFloat(item.used) || 0,
        remaining: parseFloat(item.remaining) || 0,
        notes: item.notes,
        minimum_remaining: parseFloat(item.minimum_remaining) || 0,
        custom_fields: item.custom_fields || {}
      }));

      // Отправляем каждый реагент
      await Promise.all(
        reagentsToCreate.map(async reagent => {
          try {
            await createReagent(String(labId), reagent);
          } catch (error) {
            console.error('Error creating reagent:', reagent, error);
            throw error;
          }
        })
      );

      message.success('Реагенты успешно добавлены');
      setDataSource([]);
      onSuccess?.();
      onCancel();
    } catch (error) {
      console.error('Error saving reagents:', error);
      message.error('Не удалось сохранить реагенты');
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: '№ п/п',
      dataIndex: 'number',
      key: 'number',
      width: 80,
    },
    {
      title: 'Наименование',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'name', newValue)} />
      ),
    },
    {
      title: '№ Партии/серии',
      dataIndex: 'batch',
      key: 'batch',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'batch', newValue)} />
      ),
    },
    {
      title: 'Производитель',
      dataIndex: 'manufacturer',
      key: 'manufacturer',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'manufacturer', newValue)} />
      ),
    },
    {
      title: 'Поставщик',
      dataIndex: 'supplier',
      key: 'supplier',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'supplier', newValue)} />
      ),
    },
    {
      title: 'Класс чистоты',
      dataIndex: 'frequency_class',
      key: 'frequency_class',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellSelect 
          value={value} 
          onChange={(newValue) => handleInputChange(record.key, 'frequency_class', newValue)}
          options={frequencyClassOptions}
        />
      ),
    },
    {
      title: 'Дата поступления',
      dataIndex: 'receipt_date',
      key: 'receipt_date',
      width: 200,
      render: (value: dayjs.Dayjs, record: DataType) => (
        <CellDatePicker 
          value={value} 
          onChange={(date) => handleInputChange(record.key, 'receipt_date', date || dayjs())} 
        />
      ),
    },
    {
      title: 'Срок годности',
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      width: 200,
      render: (value: dayjs.Dayjs, record: DataType) => (
        <CellDatePicker 
          value={value} 
          onChange={(date) => handleInputChange(record.key, 'expiration_date', date || dayjs())} 
        />
      ),
    },
    {
      title: 'Размер тары',
      dataIndex: 'container_size',
      key: 'container_size',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellNumberInput value={value} onChange={(newValue) => handleInputChange(record.key, 'container_size', newValue)} />
      ),
    },
    {
      title: 'Ед. измерения тары',
      dataIndex: 'container_unit',
      key: 'container_unit',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellSelect 
          value={value} 
          onChange={(newValue) => handleInputChange(record.key, 'container_unit', newValue)}
          options={containerUnitOptions}
        />
      ),
    },
    {
      title: 'Количество тарных мест',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellNumberInput value={value} onChange={(newValue) => handleInputChange(record.key, 'quantity', newValue)} />
      ),
    },
    {
      title: 'Комплект документов',
      dataIndex: 'has_documents',
      key: 'has_documents',
      width: 200,
      render: (value: boolean, record: DataType) => (
        <CellCheckbox checked={value} onChange={(checked) => handleInputChange(record.key, 'has_documents', checked)} />
      ),
    },
    {
      title: 'Допуск',
      dataIndex: 'has_admission',
      key: 'has_admission',
      width: 200,
      render: (value: boolean, record: DataType) => (
        <CellCheckbox checked={value} onChange={(checked) => handleInputChange(record.key, 'has_admission', checked)} />
      ),
    },
    {
      title: 'Прекурсор',
      dataIndex: 'is_precursor',
      key: 'is_precursor',
      width: 200,
      render: (value: boolean, record: DataType) => (
        <CellCheckbox checked={value} onChange={(checked) => handleInputChange(record.key, 'is_precursor', checked)} />
      ),
    },
    {
      title: 'Место хранения',
      dataIndex: 'storage_location',
      key: 'storage_location',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'storage_location', newValue)} />
      ),
    },
    {
      title: 'Условия хранения',
      dataIndex: 'storage_conditions',
      key: 'storage_conditions',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'storage_conditions', newValue)} />
      ),
    },
    {
      title: 'Срок хранения после вскрытия (дней)',
      dataIndex: 'opened_shelf_life',
      key: 'opened_shelf_life',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellNumberInput value={value} onChange={(newValue) => handleInputChange(record.key, 'opened_shelf_life', newValue)} />
      ),
    },
    {
      title: 'Использовано',
      dataIndex: 'used',
      key: 'used',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellNumberInput value={value} onChange={(newValue) => handleInputChange(record.key, 'used', newValue)} />
      ),
    },
    {
      title: 'Остаток',
      dataIndex: 'remaining',
      key: 'remaining',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellNumberInput value={value} onChange={(newValue) => handleInputChange(record.key, 'remaining', newValue)} />
      ),
    },
    {
      title: 'Минимальный остаток',
      dataIndex: 'minimum_remaining',
      key: 'minimum_remaining',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellNumberInput value={value} onChange={(newValue) => handleInputChange(record.key, 'minimum_remaining', newValue)} />
      ),
    },
    {
      title: 'Примечание',
      dataIndex: 'notes',
      key: 'notes',
      width: 200,
      render: (value: string, record: DataType) => (
        <CellInput value={value} onChange={(newValue) => handleInputChange(record.key, 'notes', newValue)} />
      ),
    },
    // Добавляем кастомные колонки
    ...customColumns.map(column => ({
      title: column.title,
      dataIndex: ['custom_fields', column.key],
      key: column.key,
      width: 200,
      render: (value: any, record: DataType) => {
        switch (column.type) {
          case 'text':
            return (
              <CellInput
                value={record.custom_fields?.[column.key] || ''}
                onChange={(newValue) => {
                  const newCustomFields = {
                    ...record.custom_fields,
                    [column.key]: newValue
                  };
                  handleInputChange(record.key, 'custom_fields', newCustomFields);
                }}
              />
            );
          case 'int':
            return (
              <CellNumberInput
                value={record.custom_fields?.[column.key] || ''}
                onChange={(newValue) => {
                  const newCustomFields = {
                    ...record.custom_fields,
                    [column.key]: newValue
                  };
                  handleInputChange(record.key, 'custom_fields', newCustomFields);
                }}
              />
            );
          case 'date':
            return (
              <CellDatePicker
                value={record.custom_fields?.[column.key] ? dayjs(record.custom_fields[column.key]) : null}
                onChange={(date) => {
                  const newCustomFields = {
                    ...record.custom_fields,
                    [column.key]: date ? date.format('YYYY-MM-DD') : null
                  };
                  handleInputChange(record.key, 'custom_fields', newCustomFields);
                }}
              />
            );
          case 'boolean':
            return (
              <CellCheckbox
                checked={Boolean(record.custom_fields?.[column.key])}
                onChange={(checked) => {
                  const newCustomFields = {
                    ...record.custom_fields,
                    [column.key]: checked
                  };
                  handleInputChange(record.key, 'custom_fields', newCustomFields);
                }}
              />
            );
          default:
            return (
              <CellInput
                value={record.custom_fields?.[column.key] || ''}
                onChange={(newValue) => {
                  const newCustomFields = {
                    ...record.custom_fields,
                    [column.key]: newValue
                  };
                  handleInputChange(record.key, 'custom_fields', newCustomFields);
                }}
              />
            );
        }
      }
    })),
    {
      key: "action",
      width: 60,
      render: (_: unknown, record: DataType) => (
        <Button 
          type="text" 
          icon={<DeleteOutlined style={{ color: '#f5222d' }} />}
          style={{ width: '32px', padding: 0 }}
          onClick={() => handleDelete(record.key)}
        />
      ),
    },
  ];

  return (
    <Modal
      title="Добавление множества реактивов"
      open={open}
      onCancel={onCancel}
      width={1200}
      footer={null}
      centered
    >
      <div style={{ marginBottom: 16 }}>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={handleAddRow}
          style={{ marginRight: 8 }}
        >
          Добавить строку
        </Button>
      </div>
      
      <StyledTable
        columns={columns as any}
        dataSource={dataSource}
        pagination={false}
        bordered
        scroll={{ x: 'max-content', y: 300 }}
        loading={loading}
      />
      
      <ButtonsRow justify="end" style={{ marginTop: 24 }}>
        <Space>
          <Typography.Text style={{ marginRight: 16, opacity: 0.5 }}>
            Добавлено {dataSource.length} позиций
          </Typography.Text>
          <StyledButton onClick={onCancel}>
            Отмена
          </StyledButton>
          <StyledButton type="primary" onClick={handleSave} disabled={dataSource.length === 0}>
            Сохранить
          </StyledButton>
        </Space>
      </ButtonsRow>
    </Modal>
  );
};

export default MultipleReagentsAdd; 